import axios from 'axios';
import { ColumnDisplayOptions } from '../../map/colorScale';
import { Cookies } from 'react-cookie';
import { IconName } from '../../../components_v2/dropdown/DropdownColors';
import { TagType } from '../../../components_v2/toolbarFilter/tags/Model';
import { URL_FOUNDATION, URL_JANUS } from '../../../config/keys';
import { CompanyStatus } from '../../client-companies/model/Model';
import { EmailTemplate, EmailTemplateSubmit } from '../../email-templates/model/models';
import { EmailAutomation } from './orderNotificationsSettings';
import { CreationEditionOrDeletion, Id } from './statusesAndTagsSettings';
import { OauthConfigurationState } from './thirdPartyIntegration';

const cookies = new Cookies();
const PATH = `${URL_FOUNDATION}/api`;
const token = cookies.get('id_token') || null;
axios.defaults.headers.common.Authorization = `${token}`;

export type PinStyle = {
	logo?: string | IconName,
	color?: string,
}

export type OpeningHoursRuleMode = 'off' | 'warn' | 'block';

// The opening-hours check of an hours column: an event outside the
// company's hours of this column is warned about or blocked, for the event
// types, users and teams of the scopes (null = all). Read by the server at
// event creation and edition.
export type OpeningHoursRule = {
	mode: OpeningHoursRuleMode,
	event_type_scope: number[] | null,
	user_scope: number[] | null,
	team_scope: number[] | null,
};

// the map colours of a numeric or boolean column come with the shared options
export type AdditionalColumnsDisplayOptions = ColumnDisplayOptions & {
	pin_styles?: Record<string, PinStyle>;
	opening_hours_rule?: OpeningHoursRule;
}

export async function getPkceLink(third_party: string): Promise<{ url: string, token: string, verifier: string }> {
	return axios.get(`${URL_JANUS}/api/v1/oauth/pkce-token?third_party=${third_party}`).then(res => res.data);
}

export async function getClientStatuses(): Promise<CompanyStatus[]> {
	return await axios({
		method: 'get',
		url: `${PATH}/crm/client-company-statuses`
	}).then(res => res.data);
}

export async function deleteBrand(brandId: number): Promise<null> {
	return axios.delete(`${URL_FOUNDATION}/api/brands/${brandId}`).then(res => res.data);
}

export async function deleteMapping(mappingId: number): Promise<null> {
	return axios.delete(`${URL_FOUNDATION}/api/mappings/external/${mappingId}`).then(res => res.data);
}

export async function syncTagsAndStatuses(body: { field_type: TagType, statuses: CreationEditionOrDeletion[], tags: CreationEditionOrDeletion[], additional_columns: CreationEditionOrDeletion[], additional_field_columns: CreationEditionOrDeletion[], calculated_fields: CreationEditionOrDeletion[] }): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/settings/sync-tags-and-statuses`, body).then(res => res.data);
}

type DeleteCategoryRes = {
	Ko: {
		CategoryStillHaveProducts: [number, string][]
	}
} | { Ok: [] };

export async function deleteCategory(categoryId: number): Promise<DeleteCategoryRes> {
	return axios.delete(`${URL_FOUNDATION}/api/categories/${categoryId}`).then(res => res.data);
}

export type OauthConfigurationMappings = {
	status: string,
	options: [string, string][]
}

type OauthConfiguration = {
	id: Id
	oauth_connection_id: boolean
	status: boolean
	create_sidely_to_service: boolean
	create_service_to_sidely: boolean
	update_sidely_to_service: boolean
	update_service_to_sidely: boolean
	delete_sidely_to_service: boolean
	delete_service_to_sidely: boolean
	priority: number
	created_at: Date
	updated_at?: Date
	deleted_at?: Date
	synced_at?: Date
	mappings?: OauthConfigurationMappings
}

export function getOauthConnectionConfigs(thirdParty: string): Promise<OauthConfiguration | { id: Id }> {
	return axios.get(`${URL_JANUS}/api/v1/oauth/config?third_party=${thirdParty}`).then(res => res.data ?? { id: { usync: 1 } });
}

export function getOauthConnectionConfigsPriorities(): Promise<{ id: number, name: string }[]> {
	return axios.get(`${URL_JANUS}/api/v1/oauth/config/priorities`).then(res => res.data);
}

export function syncOauthConnectionConfig(body: { third_party: string, config: CreationEditionOrDeletion<OauthConfigurationState, OauthConfigurationState> }): Promise<void> {
	return axios.post(`${URL_JANUS}/api/v1/oauth/config/sync`, body).then(res => res.data);
}

export function getEmailAutomations(): Promise<EmailAutomation[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/settings/email-automations`).then(res => res.data);
}

export function syncEmailsAutomations(body: CreationEditionOrDeletion[]): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/settings/email-automations/sync`, body).then(res => res.data);
}

export type ApiCount = {
	sent_mail_count: number,
	mail_extra: number,
	route_opimization: number,
	route_opimization_extra: number,
	photo_exports: number,
	photo_exports_extra: number,
}

export function getApiCounters(): Promise<ApiCount> {
	return axios.get(`${URL_FOUNDATION}/api/v2/api-counters`).then(res => res.data);
}

//FIXME: Devide action for Email template??
//Email Template
export async function getEmailTemplates(): Promise<EmailTemplate[]> {
	return axios.get<EmailTemplate[]>(
		`${URL_FOUNDATION}/api/v2/email-templates`
	).then(res => res.data);

}

export type FormEmailRecipient = string | { Email: string } | { AdditionalColumn: number };

export type FormEmailListItem = {
	id: number,
	emails: FormEmailRecipient[],
	view_name: string | null,
	form_name: string,
	created_at: string,
	updated_at: string | null,
}

export async function getFormEmailsList(): Promise<FormEmailListItem[]> {
	return axios.get<FormEmailListItem[]>(
		`${URL_FOUNDATION}/api/v2/forms/emails/list`
	).then(res => res.data);
}

export type FormEmail = {
	id: number,
	emails: FormEmailRecipient[],
	title: string,
	message: string,
	created_at: string,
	updated_at: string | null,
	deleted_at: string | null,
	company_id: number,
	form_id: number,
	view_id: number,
}

export async function getFormEmailById(id: number): Promise<FormEmail> {
	return axios.get<FormEmail>(`${URL_FOUNDATION}/api/v2/forms/emails/${id}`).then(res => res.data);
}

export async function putFormEmail(body: { id: number, form_id: number, view_id: number, emails: FormEmailRecipient[], title: string, message: string }): Promise<null> {
	return axios.put(`${URL_FOUNDATION}/api/v2/forms/emails`, body).then(res => res.data);
}

export async function postFormEmail(body: { form_id: number, view_id: number, emails: FormEmailRecipient[], title: string, message: string }): Promise<number> {
	return axios.post<number>(`${URL_FOUNDATION}/api/v2/forms/emails`, body).then(res => res.data);
}

export async function deleteFormEmail(id: number): Promise<null> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/forms/emails/${id}`).then(res => res.data);
}

export async function postEmailTemplate(body: EmailTemplateSubmit): Promise<number> {
	return await axios.post<number>(
		`${URL_FOUNDATION}/api/v2/email-templates`,
		body
	).then(res => res.data);
}

export async function putEmailTemplate(id: number, body: EmailTemplateSubmit): Promise<null> {
	return await axios.put<null>(
		`${URL_FOUNDATION}/api/v2/email-templates/${id}`,
		body
	).then(res => res.data);
}

export async function deleteEmailTemplate(id: number): Promise<null> {
	return await axios.delete<null>(
		`${URL_FOUNDATION}/api/v2/email-templates/${id}`,
	).then(res => res.data);
}

export type ApiToken = {
	token: string,
	allow_write: boolean
}

export async function getApiTokens(): Promise<ApiToken[]> {
	return axios.get<ApiToken[]>(
		`${URL_FOUNDATION}/api/v2/api-token`
	).then(res => res.data);
}

export async function postApiTokens(allow_write: boolean): Promise<string> {
	return axios.post<string>(
		`${URL_FOUNDATION}/api/v2/api-token`,
		{ allow_write }
	).then(res => res.data);
}

export async function deleteApiToken(token: string): Promise<undefined> {
	return axios.delete<undefined>(
		`${URL_FOUNDATION}/api/v2/api-token/${token}`,
	).then(res => res.data);
}

export type ModelUtilisation = {
	provider: string,
	model: string,
	// null when Sidely's api key was used
	api_key_id: number | null,
	input_tokens: number,
	output_tokens: number,
	utilisation: number,
}

export type ApiUtilisation = {
	route: string,
	// usage on Sidely's api key, all models together: what the limit applies to
	utilisation: number,
	utilisation_limit: number | null,
	models: ModelUtilisation[],
}

export async function getApiUtilisation(): Promise<ApiUtilisation[]> {
	return axios.get<ApiUtilisation[]>(
		`${URL_FOUNDATION}/api/v2/api-utilisation`
	).then(res => res.data);
}

type AiApiKeyWithoutId = {
	name: string,
	description?: string,
	value: string,
	provider: string,
	is_default: boolean,
}

type PutAiApiKey = {
	name?: string,
	description?: string,
	is_default?: boolean,
}

export type AiApiKey = AiApiKeyWithoutId & {
	id: number,
}

// the value and the provider of an existing key cannot be changed
export type AiApiKeyUpdate = {
	name?: string,
	description?: string,
	is_default?: boolean,
}

export type VisionSettings = {
	photo_field_ids: number[],
	// whether Vision may read cards on Sidely's own AI key (superadmin toggle)
	internal_ai_key: boolean,
	updated_at?: string | null,
}

export async function getVisionSettings(): Promise<VisionSettings> {
	return axios.get(
		`${URL_FOUNDATION}/api/v2/vision/settings`
	).then(res => res.data);
}

export async function putVisionInternalAiKey(enabled: boolean): Promise<void> {
	return axios.put(
		`${URL_FOUNDATION}/api/v2/vision/settings/internal-ai-key`, { enabled }
	).then(res => res.data);
}

export async function getAiApiKeys(): Promise<AiApiKey[]> {
	return axios.get(
		`${URL_FOUNDATION}/api/v2/ai/api-key`
	).then(res => res.data);
}

export async function postAiApiKeys(body: AiApiKeyWithoutId): Promise<number> {
	return axios.post(
		`${URL_FOUNDATION}/api/v2/ai/api-key`, body
	).then(res => res.data);
}

export async function putAiApiKeys(id: AiApiKey['id'], body: PutAiApiKey): Promise<void> {
	return axios.put(
		`${URL_FOUNDATION}/api/v2/ai/api-key/${id}`, body
	).then(res => res.data);
}

export async function deleteAiApiKeys(id: AiApiKey['id']): Promise<void> {
	return axios.delete(
		`${URL_FOUNDATION}/api/v2/ai/api-key/${id}`
	).then(res => res.data);
}

export type AdditionalValuesPathBody = {
	label: string,
	field_id: number,
	additional_column_ids: number[],
	options: object | null,
	locked?: boolean,
}

export type AdditionalValuesPath = AdditionalValuesPathBody & { id: number, locked: boolean };

export function getAdditionalValuesPaths(): Promise<AdditionalValuesPath[]> {
	return axios.get(`${URL_FOUNDATION}/api/v2/additional-values-paths`).then(res => res.data);
}

export function postAdditionalValuesPath(body: AdditionalValuesPathBody): Promise<number> {
	return axios.post(`${URL_FOUNDATION}/api/v2/additional-values-paths`, body).then(res => res.data);
}

export function putAdditionalValuesPath(id: number, body: AdditionalValuesPathBody): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/additional-values-paths/${id}`, body).then(() => undefined);
}

export function deleteAdditionalValuesPath(id: number): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/additional-values-paths/${id}`).then(() => undefined);
}
